import { baseURL } from './constants/env';

import dayjs from 'dayjs';

export const getTimezone = () => {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
};

export const parseUtcTime = (dateStr?: string | Date) => {
    if (!dateStr) return dayjs();
    const str = typeof dateStr === 'string' ? dateStr : dateStr.toISOString();
    // Append 'Z' if it's missing to force UTC interpretation
    if (typeof str === 'string' && !str.endsWith('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
        return dayjs(`${str}Z`);
    }
    return dayjs(str);
};

export const getAvatarUrl = (url?: string | null) => {
    if (!url || typeof url !== 'string' || !url.trim()) return undefined;
    const trimmed = url.trim();
    if (trimmed.startsWith('blob:') || trimmed.startsWith('data:image')) return trimmed;

    let rootUrl = 'http://localhost:8000';
    if (typeof window !== 'undefined') {
        const h = window.location.hostname;
        if (h === 'app.trogiang.online' || h.endsWith('.trogiang.online')) {
            rootUrl = 'https://api.trogiang.online';
        } else if (h === 'app-trogiang.icitech.net' || h.endsWith('.icitech.net')) {
            rootUrl = 'https://api-trogiang.icitech.net';
        } else if (h !== 'localhost' && h !== '127.0.0.1') {
            rootUrl = `${window.location.protocol}//${h}:8000`;
        } else {
            try {
                rootUrl = new URL(baseURL).origin;
            } catch (e) {
                rootUrl = 'http://localhost:8000';
            }
        }
    } else {
        try {
            rootUrl = new URL(baseURL).origin;
        } catch (e) {
            rootUrl = 'http://localhost:8000';
        }
    }

    // Check if the URL points to /upload/...
    const uploadIndex = trimmed.indexOf('/upload/');
    if (uploadIndex !== -1) {
        return `${rootUrl}${trimmed.substring(uploadIndex)}`;
    }
    if (trimmed.startsWith('upload/')) {
        return `${rootUrl}/${trimmed}`;
    }

    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
        if (trimmed.includes('localhost:8000') || trimmed.includes('127.0.0.1:8000')) {
            return trimmed.replace(/https?:\/\/(localhost|127\.0\.0\.1):8000/, rootUrl);
        }
        return trimmed;
    }

    const cleanPath = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    return `${rootUrl}${cleanPath}`;
};

export const getImageUrl = getAvatarUrl;

export const getVietQrProxyUrl = (url?: string | null): string => {
    if (!url || typeof url !== 'string' || !url.trim()) return '';
    const trimmed = url.trim();
    if (trimmed.includes('img.vietqr.io') || trimmed.includes('api.vietqr.io')) {
        let rootUrl = 'http://localhost:8000';
        if (typeof window !== 'undefined') {
            const h = window.location.hostname;
            if (h === 'app.trogiang.online' || h.endsWith('.trogiang.online')) {
                rootUrl = 'https://api.trogiang.online';
            } else if (h === 'app-trogiang.icitech.net' || h.endsWith('.icitech.net')) {
                rootUrl = 'https://api-trogiang.icitech.net';
            } else if (h !== 'localhost' && h !== '127.0.0.1') {
                rootUrl = `${window.location.protocol}//${h}:8000`;
            }
        }
        return `${rootUrl}/api/v1/finance/vietqr-proxy?url=${encodeURIComponent(trimmed)}`;
    }
    return trimmed;
};

export const generateSlug = (text: string): string => {
    return text
        .toString()
        .toLowerCase()
        .normalize('NFD') // Remove diacritics
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[đĐ]/g, 'd') // Handle Vietnamese D
        .replace(/\s+/g, '-') // Replace spaces with -
        .replace(/[^\w-]+/g, '') // Remove all non-word chars
        .replace(/--+/g, '-') // Replace multiple - with single -
        .replace(/^-+/, '') // Trim - from start of text
        .replace(/-+$/, ''); // Trim - from end of text
};

export const DEFAULT_POST_THUMBNAIL = "https://images.unsplash.com/photo-1544717305-2782549b5136?q=80&w=800&auto=format&fit=crop";

export const getPostThumbnail = (post?: any, fallback = DEFAULT_POST_THUMBNAIL): string => {
    if (!post) return fallback;

    if (typeof post.thumbnail_url === 'string' && post.thumbnail_url.trim()) {
        const resolved = getAvatarUrl(post.thumbnail_url.trim());
        if (resolved) return resolved;
    }

    if (Array.isArray(post.content_blocks) && post.content_blocks.length > 0) {
        for (const block of post.content_blocks) {
            if (!block) continue;
            if (block.type === 'gallery' && Array.isArray(block.data?.images) && block.data.images.length > 0) {
                for (const img of block.data.images) {
                    const url = typeof img === 'string' ? img : (img?.url || img?.file_url);
                    if (url && typeof url === 'string' && url.trim()) {
                        const resolved = getAvatarUrl(url.trim());
                        if (resolved) return resolved;
                    }
                }
            }
            if (block.type === 'about_profile' && block.data?.avatar_url) {
                const resolved = getAvatarUrl(block.data.avatar_url);
                if (resolved) return resolved;
            }
            if (block.type === 'rich_text' && typeof block.data?.content === 'string') {
                const match = block.data.content.match(/<img[^>]+src=["']([^"']+)["']/i);
                if (match && match[1]) {
                    const resolved = getAvatarUrl(match[1]);
                    if (resolved) return resolved;
                }
            }
        }
    }

    if (typeof post.content === 'string' && post.content) {
        const match = post.content.match(/<img[^>]+src=["']([^"']+)["']/i);
        if (match && match[1]) {
            const resolved = getAvatarUrl(match[1]);
            if (resolved) return resolved;
        }
    }

    if (Array.isArray(post.attachments) && post.attachments.length > 0) {
        for (const att of post.attachments) {
            const fileUrl = att?.url || att?.file_url;
            if (fileUrl && typeof fileUrl === 'string') {
                if (/\.(jpg|jpeg|png|webp|gif|svg)$/i.test(fileUrl)) {
                    const resolved = getAvatarUrl(fileUrl);
                    if (resolved) return resolved;
                }
            }
        }
    }

    return fallback;
};

export const ROLE_DISPLAY_MAP: Record<string, string> = {
    ADMIN: 'Quản trị viên hệ thống',
    COMPANY_ADMIN: 'Quản trị viên trường học',
    TEACHER: 'Giáo viên',
    PARENT: 'Phụ huynh',
    STUDENT: 'Học sinh',
    SALES: 'Nhân viên kinh doanh',
    ORG_SALES: 'Nhân viên kinh doanh'
};

export const getRoleDisplayName = (role?: string | null): string => {
    if (!role) return '-';
    return ROLE_DISPLAY_MAP[role.toUpperCase()] || role;
};

export const getRoleColor = (role?: string | null): "default" | "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    switch (role?.toUpperCase()) {
        case 'ADMIN': return 'error';
        case 'COMPANY_ADMIN': return 'primary';
        case 'TEACHER': return 'info';
        case 'STUDENT': return 'success';
        case 'PARENT': return 'warning';
        case 'SALES':
        case 'ORG_SALES': return 'secondary';
        default: return 'default';
    }
};
